import { ApexOptions } from 'apexcharts';
import Big from 'big.js';
import { isValid, parse } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { ComponentType, Suspense, lazy, useMemo } from 'react';
import type { Props } from 'react-apexcharts';
import { renderToString } from 'react-dom/server';
import { useIsMobile } from '../hooks';
import { ApexChartSeries } from '../types';
import { formatNumbers, goodFormatNumber } from '../utils';
import { Spinner } from './Spinner';

const Chart = lazy(() =>
  import('react-apexcharts').then(module => ({
    default: ((module as any).default?.default ?? (module as any).default ?? module) as any,
  })),
) as unknown as ComponentType<Props>;

dayjs.extend(utc);

export interface BalanceChartProps {
  type?: Props['type'];
  fill?: boolean;
  data: ApexChartSeries[];
  options?: Partial<ApexOptions>;
  height?: string;
  withDateTime?: boolean;
  rotateLabel?: number;
  countsLabel?: number;
  maxHeightLabel?: number;
  minHeightLabel?: number;
  colors?: string[];
}

export const BalanceChart = ({
  data,
  type = 'area',
  fill = true,
  options = {},
  height = '100%',
  withDateTime = false,
  rotateLabel = 0,
  countsLabel = 6,
  maxHeightLabel,
  minHeightLabel,
  colors,
}: BalanceChartProps) => {
  const { isMobile } = useIsMobile();
  const resolveOptions = useMemo<ApexOptions>(() => {
    const vals = data.flatMap(item => item.data.map<number>(d => d.y));
    let max = Math.max(...vals, 0);
    max = max + max * 0.1; //Fixed: Error: <path> attribute d: Expected number
    return {
      chart: {
        toolbar: {
          show: false,
          tools: {
            download: false,
          },
        },
        type: type,
        redrawOnParentResize: true,
        redrawOnWindowResize: true,
        width: '100%',
        height: height,
        zoom: {
          enabled: false,
        },
      },
      colors: colors || ['#0369A1'],
      stroke: {
        width: 2,
        curve: 'straight',
        lineCap: 'round',
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        custom: ({ dataPointIndex, w }) => {
          const series = w.config.series as {
            name: string;
            currency?: string;
            data: { x: number; y: number }[];
          }[];
          const timestamp = series[0]?.data[dataPointIndex]?.x;
          if (!timestamp) return '';

          const utcDate = dayjs.utc(new Date(timestamp)).toISOString();
          const toDateString = new Date(utcDate).toDateString();
          const convertedTime = dayjs.utc(new Date(timestamp)).format('HH:mm:ss');
          const seriesData = series.map((s, idx) => {
            const y = s.data[dataPointIndex]?.y ?? null;
            return {
              name: s.name,
              currency: s.currency,
              color: w.config.colors?.[idx] || '#000',
              value: y,
            };
          });

          return renderToString(
            <CustomTooltip
              series={seriesData}
              date={toDateString}
              time={withDateTime ? convertedTime : ''}
            />,
          );
        },
      },
      xaxis: {
        tickAmount: countsLabel,
        axisTicks: {
          show: false,
        },
        axisBorder: {
          show: true,
          strokeWidth: 1,
          color: '#E5E7EB',
          offsetY: -1,
        },
        labels: {
          offsetY: 2,
          maxHeight: maxHeightLabel,
          minHeight: minHeightLabel,
          rotate: isMobile ? -60 : rotateLabel,
          hideOverlappingLabels: true,
          showDuplicates: false,
          style: {
            fontSize: '12px',
            fontWeight: '400',
            colors: '#A3A3A3',
          },
          formatter: (value: string) => {
            return dayjs.utc(value).isValid() ? dayjs.utc(value).format('DD-MM') : value;
          },
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        labels: {
          offsetX: -10,
          style: {
            fontSize: '12px',
            fontWeight: '400',
            colors: '#A3A3A3',
          },
          formatter: value => {
            return value ? formatNumbers(value) : String(value);
          },
        },
        crosshairs: {
          show: true,
        },
        tickAmount: 4,
        min: 0,
        max,
      },
      fill: (fill
        ? {
            type: 'gradient',
            gradient: {
              type: 'vertical',
              opacityFrom: 0.34,
              opacityTo: 0,
            },
          }
        : {}) as ApexFill,
      legend: {
        position: 'top',
        horizontalAlign: 'left',
      },
      ...(options || {}),
    };
  }, [
    type,
    height,
    colors,
    countsLabel,
    maxHeightLabel,
    minHeightLabel,
    isMobile,
    rotateLabel,
    fill,
    options,
    withDateTime,
    data,
  ]);

  const series = useMemo<ApexAxisChartSeries>(() => {
    const result =
      data?.map(item => {
        return {
          ...item,
          data:
            (item?.data as { y: number; x: string }[])?.map(info => {
              const dateObj = parse(info?.x, 'dd-MM-yyyy HH:mm:ss', new Date());
              const dateObj2 = parse(info?.x, 'dd-MM-yyyy', new Date());
              const dateObj3 = parse(info?.x, 'yyyy-MM-dd', new Date());

              const xDate = isValid(dateObj)
                ? dateObj
                : isValid(dateObj2)
                ? dateObj2
                : isValid(dateObj3)
                ? dateObj3
                : info?.x;

              const x = dayjs.utc(xDate).toISOString();
              const y = Number(new Big(+info?.y || 0).round(3, 1).valueOf());
              return {
                x,
                y,
              };
            }) || [],
        };
      }) || [];
    return result;
  }, [data]);

  return (
    <Suspense
      fallback={
        <div className="min-h-[250px] h-full w-full flex justify-center items-center">
          <Spinner size="lg" />
        </div>
      }
    >
      <Chart width={'100%'} height={height} type={type} options={resolveOptions} series={series} />
    </Suspense>
  );
};

interface TooltipProps {
  series: { name: string; color: string; currency?: string; value: number | null }[];
  date: string;
  time?: string;
}

const CustomTooltip = ({ series, date, time }: TooltipProps) => {
  return (
    <div className="flex flex-col min-w-[250px] rounded-xl p-4 space-y-2 bg-white">
      <div className="w-full flex items-center justify-between">
        <p className="text-[14px] leading-[22px] font-medium">{date}</p>
        {time && <p className="text-[12px] leading-[22px] text-gray-500">{time}</p>}
      </div>

      {series.map((s, idx) => (
        <div key={idx} className="w-full flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="w-[6px] h-[6px] rounded-full" style={{ backgroundColor: s.color }} />
            <span className="text-[14px] leading-[22px] text-gray-500">{s.name}</span>
          </div>
          <div className="text-left">
            <span className="text-[14px] leading-[22px]">
              {s.value !== null ? `${goodFormatNumber(s.value)} ${s.currency || '$'}` : '—'}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};
